import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Database,
  Brain,
  Monitor,
  Link2,
  Shield,
  TrendingUp,
  Layers,
  Cpu,
  Layout,
  Cable,
  Lock,
  Wrench,
  Clock,
  DollarSign,
  Target,
  Phone,
  RefreshCw,
  BookOpen,
  ArrowRight,
  Check,
  X,
  ChevronRight,
  Store,
  Stethoscope,
  Factory,
  PhoneCall,
  Landmark,
  UtensilsCrossed,
  Calendar,
  Loader,
  Building2
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= CONSULTATION MODAL =============
const ConsultationModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    industry: '',
    currentTools: '',
    timeline: '',
    package: selectedPlan || '',
    goals: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  // Update package when selectedPlan changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, package: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.goals) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'Full Stack AI Solutions',
          message: `Full Stack AI Consultation Request\n\nCompany: ${formData.company}\nCompany Size: ${formData.employees || 'Not specified'}\nIndustry: ${formData.industry || 'Not specified'}\nCurrent Tools: ${formData.currentTools || 'Not specified'}\nTimeline: ${formData.timeline || 'Not specified'}\nPackage Interest: ${formData.package || 'Not specified'}\n\nBusiness Goals:\n${formData.goals}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', employees: '', industry: '', currentTools: '', timeline: '', package: '', goals: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Consultation error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', employees: '', industry: '', currentTools: '', timeline: '', package: selectedPlan || '', goals: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Call Scheduled!</h3>
            <p className="text-gray-400 mb-6">
              Thanks for reaching out! We'll contact you within 24 hours to schedule your free consultation call.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Layers size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Full Stack Solutions</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Schedule Free Call</h2>
            <p className="text-teal-400 mb-6">Let's discuss your complete AI solution</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your work email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Best number to reach you"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Company Size</label>
                  <select
                    value={formData.employees}
                    onChange={(e) => handleChange('employees', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="1-10" className="bg-slate-950">1-10</option>
                    <option value="11-50" className="bg-slate-950">11-50</option>
                    <option value="51-200" className="bg-slate-950">51-200</option>
                    <option value="201-500" className="bg-slate-950">201-500</option>
                    <option value="500+" className="bg-slate-950">500+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Retail" className="bg-slate-950">Retail</option>
                    <option value="Healthcare" className="bg-slate-950">Healthcare</option>
                    <option value="Manufacturing" className="bg-slate-950">Manufacturing</option>
                    <option value="Finance" className="bg-slate-950">Finance</option>
                    <option value="Restaurant" className="bg-slate-950">Restaurant</option>
                    <option value="Services" className="bg-slate-950">Services</option>
                    <option value="Other" className="bg-slate-950">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Current Tools/Software</label>
                <input
                  type="text"
                  value={formData.currentTools}
                  onChange={(e) => handleChange('currentTools', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="e.g., QuickBooks, Salesforce, Excel"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Timeline</label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="ASAP" className="bg-slate-950">ASAP</option>
                    <option value="1-3 months" className="bg-slate-950">1-3 months</option>
                    <option value="3-6 months" className="bg-slate-950">3-6 months</option>
                    <option value="Just exploring" className="bg-slate-950">Just exploring</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Package</label>
                  <select
                    value={formData.package}
                    onChange={(e) => handleChange('package', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Starter ($40K)" className="bg-slate-950">Starter ($40K)</option>
                    <option value="Business ($80K)" className="bg-slate-950">Business ($80K)</option>
                    <option value="Enterprise ($200K+)" className="bg-slate-950">Enterprise ($200K+)</option>
                    <option value="Not sure yet" className="bg-slate-950">Not sure yet</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">What do you want AI to help with? *</label>
                <textarea
                  value={formData.goals}
                  onChange={(e) => handleChange('goals', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Describe what you'd like to achieve with AI (e.g., automate inventory, predict sales, improve customer service)"
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Calendar size={18} />
                    Schedule Free Call
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onScheduleCall }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#benefits" className="text-sm text-gray-300 hover:text-white transition">Benefits</a>
            <a href="#pricing" className="text-sm text-gray-300 hover:text-white transition">Pricing</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onScheduleCall}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Schedule Free Call
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onScheduleCall }) => {
  const stats = [
    { value: '100%', label: 'Complete Solutions' },
    { value: '50+', label: 'Happy Businesses' },
    { value: '60%', label: 'Average Time Saved' },
    { value: '24/7', label: 'Always Working' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['Complete Systems', 'Easy to Understand', 'All Included'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">Full Stack</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            AI Solutions
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Everything You Need for AI - All in One Place
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Think of us as your one-stop shop for AI. Instead of buying pieces from different places 
          and trying to make them work together, we build the whole thing for you. From collecting 
          your data to putting smart technology in your hands—we handle every single step. No tech 
          degree required to understand what we do!
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onScheduleCall}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Schedule Free Call <Calendar size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('examples').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            See Examples
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-teal-300" size={32} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: Database,
      title: 'Data Setup & Management',
      description: "We organize all your information (data) so the AI can understand it. Think of it like organizing a messy closet so you can actually find your clothes.",
      features: [
        'Collect data from all your sources',
        'Clean up messy or incomplete information',
        'Organize everything in a smart way',
        'Keep your data safe and secure',
        'Make sure everything updates automatically'
      ]
    },
    {
      icon: Brain,
      title: 'Smart Brain Building',
      description: "This is where we create the \"smart\" part. We train computer systems to recognize patterns and make decisions—kind of like teaching a really fast student.",
      features: [
        'Build the AI to solve your specific problem',
        'Train it using your information',
        'Test to make sure it works correctly',
        'Make it smarter over time',
        'Explain how it makes decisions'
      ]
    },
    {
      icon: Monitor,
      title: 'User-Friendly Interface',
      description: "We create the screens and buttons you actually use. No complicated tech stuff—just simple, easy-to-understand interfaces anyone can use.",
      features: [
        'Easy-to-use dashboards',
        'Works on computers, tablets, and phones',
        'Clear instructions and labels',
        'Shows results in plain English',
        'Looks professional and modern'
      ]
    },
    {
      icon: Link2,
      title: 'Connection to Your Tools',
      description: "We plug the AI into the software you already use—like your customer database, email, or accounting system. Everything talks to each other automatically.",
      features: [
        'Connect to your existing software',
        'Share information automatically',
        'No need to switch between programs',
        'Works with popular business tools',
        'Saves you from repetitive work'
      ]
    },
    {
      icon: Shield,
      title: 'Security & Protection',
      description: "We lock everything down tight. Your information stays private, hackers stay out, and everything runs safely.",
      features: [
        'Password protection and encryption',
        'Control who sees what information',
        'Regular security updates',
        'Backup copies of everything',
        'Follow all privacy laws and rules'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Ongoing Support & Updates',
      description: "We don't just build it and leave. We stick around to help, fix problems, and make improvements. Like having a personal tech team on speed dial.",
      features: [
        'Answer questions anytime',
        'Fix problems quickly',
        'Add new features as you grow',
        'Train your team on updates',
        'Regular check-ins to improve things'
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            What We Do
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Complete AI Systems
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            We build entire AI solutions from start to finish. Like getting a fully furnished house 
            instead of just buying bricks. Everything works together perfectly right from day one.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= EXPLAINER SECTION =============
const ExplainerSection = () => {
  const steps = [
    { number: 1, icon: Layers, title: 'The Foundation', description: "We set up the basic structure—servers, databases, and storage. This is like the foundation and walls of a house." },
    { number: 2, icon: Cpu, title: 'The Brain', description: "This is the AI itself—the smart technology that thinks and makes decisions. Like the electrical and plumbing systems." },
    { number: 3, icon: Layout, title: 'The Interface', description: "The screens, buttons, and dashboards you actually touch and see. Like the interior design and furniture." },
    { number: 4, icon: Cable, title: 'The Connections', description: "How everything plugs together—your AI, your existing software, and other tools." },
    { number: 5, icon: Lock, title: 'The Security', description: "Locks, passwords, and protection for your data. Like the security system and fence around your house." },
    { number: 6, icon: Wrench, title: 'The Maintenance', description: "Regular updates, fixes, and improvements to keep everything running smoothly." }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Simple Explanation
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What "Full Stack" Means
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            "Full Stack" is tech speak for "we do everything." Here's what that actually means 
            in regular language—think of it like building a house from foundation to furniture.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="group p-6 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-teal-500/30 transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <motion.div
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center mb-4 text-white font-bold text-lg"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  {step.number}
                </motion.div>
                <h3 className="text-lg font-bold text-teal-400 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    { icon: Clock, title: 'Faster Launch', description: "Everything's built to work together from the start. No waiting for different pieces to connect. Ready to go immediately." },
    { icon: DollarSign, title: 'Lower Total Cost', description: "One team, one bill, one solution. Way cheaper than hiring separate companies for each piece." },
    { icon: Target, title: 'Everything Works Together', description: "No compatibility issues or things breaking when one piece updates. Everything matches and fits perfectly." },
    { icon: Phone, title: 'One Point of Contact', description: "Got a problem? Call us. No more \"that's not our department\" runaround. One phone number for all questions." },
    { icon: RefreshCw, title: 'Easy Updates', description: "When we improve one part, everything else adapts automatically. No juggling multiple vendors." },
    { icon: BookOpen, title: 'Simpler Training', description: "Your team learns one system, not five different tools. Everyone's up to speed in days, not months." }
  ];

  return (
    <section id="benefits" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Why Choose Full Stack
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Benefits You'll Actually Notice
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Here's what getting a complete AI solution means for your business in terms 
            anyone can understand. Real benefits, no fluff.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full"
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <benefit.icon className="text-teal-300" size={28} />
                </div>
                <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= COMPARISON SECTION =============
const ComparisonSection = () => {
  const comparisons = [
    { aspect: 'Setup Time', fullStack: '6-12 weeks', piecemeal: '4-8 months' },
    { aspect: 'Everything Works Together', fullStack: true, piecemeal: false },
    { aspect: 'One Point of Contact', fullStack: true, piecemeal: false },
    { aspect: 'Typical Cost', fullStack: '$50K-$150K', piecemeal: '$100K-$300K+' },
    { aspect: 'Maintenance Hassle', fullStack: 'Low - We handle it', piecemeal: 'High - You coordinate' },
    { aspect: 'Updates & Improvements', fullStack: 'Automatic & Smooth', piecemeal: 'Manual & Complicated' },
    { aspect: 'Training Required', fullStack: '1-2 days', piecemeal: '1-2 weeks' },
    { aspect: 'Risk of Things Breaking', fullStack: 'Very Low', piecemeal: 'High' }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            See The Difference
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Full Stack vs. Piecemeal
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Here's a straightforward comparison showing why getting everything from one place 
            makes more sense than piecing things together yourself.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-3 bg-gradient-to-r from-blue-500/10 via-teal-500/10 to-emerald-500/10 border-b border-white/10">
            <div className="p-6 text-center">
              <div className="text-sm font-bold text-gray-400 uppercase tracking-wider">Aspect</div>
            </div>
            <div className="p-6 text-center border-l border-white/10">
              <div className="text-lg font-bold text-teal-400">Full Stack (Us)</div>
              <div className="text-xs text-gray-400">One complete solution</div>
            </div>
            <div className="p-6 text-center border-l border-white/10">
              <div className="text-lg font-bold text-gray-400">Piecemeal</div>
              <div className="text-xs text-gray-500">Multiple vendors</div>
            </div>
          </div>

          {comparisons.map((row, index) => (
            <div
              key={index}
              className={`grid grid-cols-3 ${index !== comparisons.length - 1 ? 'border-b border-white/5' : ''}`}
            >
              <div className="p-5 flex items-center justify-center">
                <span className="text-gray-300 font-medium text-center">{row.aspect}</span>
              </div>
              <div className="p-5 flex items-center justify-center border-l border-white/10 bg-emerald-500/5">
                {typeof row.fullStack === 'boolean' ? (
                  row.fullStack ? (
                    <Check className="text-emerald-400" size={24} />
                  ) : (
                    <X className="text-gray-600" size={24} />
                  )
                ) : (
                  <span className="text-emerald-400 font-medium text-center">{row.fullStack}</span>
                )}
              </div>
              <div className="p-5 flex items-center justify-center border-l border-white/10">
                {typeof row.piecemeal === 'boolean' ? (
                  row.piecemeal ? (
                    <Check className="text-emerald-400" size={24} />
                  ) : (
                    <X className="text-gray-600" size={24} />
                  )
                ) : (
                  <span className="text-gray-400 text-center">{row.piecemeal}</span>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============= PRICING SECTION =============
const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      tier: 'Starter Package',
      price: '$40K',
      period: 'One-Time + $2K/month',
      featured: false,
      planValue: 'Starter ($40K)',
      features: [
        'Complete AI system setup',
        'Simple dashboard to view results',
        'Connect to 3 existing tools',
        'Basic security and backups',
        'Email support',
        'Monthly performance reports',
        'Setup takes 6-8 weeks'
      ]
    },
    {
      tier: 'Business Package',
      price: '$80K',
      period: 'One-Time + $4K/month',
      featured: true,
      planValue: 'Business ($80K)',
      features: [
        'Everything in Starter, plus:',
        'Advanced AI capabilities',
        'Custom-designed interface',
        'Connect to unlimited tools',
        'Mobile app access',
        'Priority phone support',
        'Team training included',
        'Quarterly strategy sessions'
      ]
    },
    {
      tier: 'Enterprise Package',
      price: '$200K+',
      period: 'One-Time + Custom/month',
      featured: false,
      planValue: 'Enterprise ($200K+)',
      features: [
        'Everything in Business, plus:',
        'Multi-location support',
        'Advanced analytics & reporting',
        'Dedicated account manager',
        '24/7 phone support',
        'Custom features built for you',
        'Unlimited training sessions',
        'White-glove service'
      ]
    }
  ];

  return (
    <section id="pricing" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, Honest Pricing
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            No hidden fees, no surprises. Pick a package based on your needs and budget. 
            Everything is included—no add-ons or upsells.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full text-xs font-bold text-slate-900">
                    BEST VALUE
                  </span>
                </div>
              )}

              <div className={`relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${
                plan.featured 
                  ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' 
                  : 'border-white/10 hover:border-teal-500/30'
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-3xl ${
                  plan.featured 
                    ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500/50 via-teal-500/50 to-emerald-500/50'
                }`} />

                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">
                  {plan.tier}
                </h3>

                <div className="text-center mb-6">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {plan.period}
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectPlan(plan.planValue)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                      : 'border border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50'
                  }`}
                >
                  Get Started
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= EXAMPLES SECTION =============
const ExamplesSection = () => {
  const examples = [
    {
      icon: Store,
      title: 'Local Retail Chain',
      description: 'They wanted to predict which products would sell best at each store location. We built a complete system that looks at sales history, weather, and trends.',
      result: '40% less wasted inventory, 25% increase in sales, saved $500K in the first year.'
    },
    {
      icon: Stethoscope,
      title: 'Medical Clinic',
      description: 'Their appointment scheduling was chaotic. We created a smart scheduling system that predicts appointment lengths and optimizes the calendar.',
      result: 'Patient wait time cut by 50%, doctors see 20% more patients, satisfaction jumped to 4.7 stars.'
    },
    {
      icon: Factory,
      title: 'Manufacturing Company',
      description: 'Their machines kept breaking down unexpectedly. We built a monitoring system that predicts when machines need maintenance.',
      result: '90% fewer surprise breakdowns, $2M saved annually in emergency repairs.'
    },
    {
      icon: PhoneCall,
      title: 'Customer Service Center',
      description: 'They got swamped with the same questions. We built an AI assistant that answers common questions instantly, 24/7.',
      result: 'Handle 5X more inquiries without hiring, instant answers for 80% of questions.'
    },
    {
      icon: Landmark,
      title: 'Credit Union',
      description: 'Processing loan applications took weeks. We built a system that reads documents automatically and makes preliminary decisions.',
      result: 'Loan approval dropped from 15 days to 2 days, handle 3X more applications.'
    },
    {
      icon: UtensilsCrossed,
      title: 'Restaurant Chain',
      description: 'They threw away tons of food daily. We built a prediction system that forecasts exactly how much food to prepare.',
      result: '70% reduction in food waste, saved $300K annually, favorite items always available.'
    }
  ];

  return (
    <section id="examples" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Real Examples
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            See It In Action
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Here are real examples from businesses like yours who got complete AI solutions. 
            See what's possible when everything works together perfectly.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {examples.map((example, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-teal-500/30 transition-all duration-300 h-full flex flex-col">
                <div className="bg-gradient-to-r from-blue-500/20 via-teal-500/20 to-emerald-500/20 p-8 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-4">
                    <example.icon className="text-teal-300" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-teal-400">
                    {example.title}
                  </h3>
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-grow">
                    {example.description}
                  </p>

                  <div className="bg-emerald-500/10 border-l-4 border-emerald-500 rounded-r-xl p-4">
                    <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">The Result:</p>
                    <p className="text-sm text-gray-300">
                      {example.result}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onScheduleCall }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Let's talk about your business and see how a complete AI solution can help. 
            No tech talk, just a straightforward conversation about your goals.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onScheduleCall}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Schedule Free Call <Phone size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              Complete AI solutions that work together perfectly from day one.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/ai-consulting-services" className="hover:text-teal-400 transition">Consulting</Link></li>
              <li><Link to="/full-stack-ai-solutions" className="hover:text-teal-400 transition">Full Stack Solutions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function FullStackAISolutionsPage() {
  const [consultationModal, setConsultationModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleScheduleCall = (plan = null) => {
    setSelectedPlan(plan);
    setConsultationModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onScheduleCall={() => handleScheduleCall()} />
      <Hero onScheduleCall={() => handleScheduleCall()} />
      <ServicesSection />
      <ExplainerSection />
      <BenefitsSection />
      <ComparisonSection />
      <PricingSection onSelectPlan={handleScheduleCall} />
      <ExamplesSection />
      <FinalCTA onScheduleCall={() => handleScheduleCall()} />
      <Footer />

      {/* Consultation Modal */}
      <ConsultationModal
        isOpen={consultationModal}
        onClose={() => setConsultationModal(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
}